/**
 * The per-entity predicate-question loop — the same shape as onboarding's findings loop, applied to an
 * employee (per-hire predicates) or a case (per-case predicates). `base` is the entity collection path
 * (e.g. "/api/employees" or "/api/cases"); the server routes answers into the entity's Predicates map.
 */

import { apiGet, apiPost } from '../apiClient';
import type { NextQuestion } from './onboarding';

/** GET {base}/{id}/questions/next — the first unanswered predicate question for the entity, or done. */
export function getEntityQuestions(base: string, id: string): Promise<NextQuestion> {
  return apiGet<NextQuestion>(`${base}/${id}/questions/next`);
}

/** One applicable question with the answer currently stored on the entity (null = never answered). */
export interface EntityQuestionValue {
  key: string;
  prompt: string;
  value: boolean | null;
  /** Plain-English explanation shown under the question; absent when the predicate has none. */
  help?: string | null;
  /**
   * The nearest question ON SCREEN that this one hangs off; absent at top level. The relationship
   * itself, not a level: derive indentation with questionDepth() so it can never disagree with the
   * structure. Not the raw predicate parent, which is often a derived fact that is never shown.
   */
  parentKey?: string | null;
  /**
   * A public-register suggestion for an UNANSWERED question: pre-select it and show
   * `suggestionEvidence` so the user can see where it came from and change it. Absent when there is no
   * suggestion, and never present for a question that already has an answer — a register never overrides
   * something the user said.
   */
  suggestedValue?: boolean | null;
  /** Why the register suggests that, e.g. "SIC 78200: temporary employment agency activities". */
  suggestionEvidence?: string | null;
  /** Which register it came from: 'companies-house' | 'fca'. Named honestly in the UI — attributing an
   *  FCA finding to Companies House would misstate the provenance the user is relying on. */
  suggestionSource?: string | null;
}

/**
 * How deeply a question is nested, derived by walking its ancestry within the list it belongs to.
 * Guarded against a cycle so a malformed chain degrades to a flat list rather than hanging.
 */
export function questionDepth(question: EntityQuestionValue, all: EntityQuestionValue[]): number {
  const byKey = new Map(all.map((q) => [q.key, q]));
  let depth = 0;
  let current = question.parentKey;
  const seen = new Set<string>([question.key]);
  while (current && !seen.has(current)) {
    seen.add(current);
    depth += 1;
    current = byKey.get(current)?.parentKey ?? null;
  }
  return depth;
}

export interface EntityQuestionsResponse {
  questions: EntityQuestionValue[];
}

/**
 * GET {base}/{id}/questions — EVERY currently-applicable question with its stored answer, for an edit
 * form. Contrast getEntityQuestions, which walks them one at a time during capture.
 */
export function listEntityQuestions(base: string, id: string): Promise<EntityQuestionsResponse> {
  return apiGet<EntityQuestionsResponse>(`${base}/${id}/questions`);
}

/** POST {base}/{id}/questions/answer — stores the boolean answer and returns the next question (or done). */
export function answerEntityQuestion(
  base: string,
  id: string,
  key: string,
  value: string,
): Promise<NextQuestion> {
  return apiPost<NextQuestion>(`${base}/${id}/questions/answer`, { key, value });
}
